// @module Ames.RentFromAnywhere.RentFromAnywhere
// Rent From Anywhere - Terms & Conditions checkbox on the checkout Review step.
//
// Shown ONLY when the cart has rental lines (same detection as the payment
// restriction / rental SO split). Required: "Place Order" is blocked until the
// shopper ticks it. For a cart without rental lines the module is inactive:
// it is not rendered and never blocks the order.
//
// Registered from Ames.RentFromAnywhere.RentFromAnywhere.js with
// Checkout.addModuleToStep({ step_url: "review", ... }).
//
// IMPORTANT: no hard dependency on "Wizard.Module" (checkout-only module) -
// see buildClass() below. A hard dependency breaks the shopping/myaccount bundles.
define("Ames.RentFromAnywhere.Terms.Module", [
  "ames_rentfromanywhere_terms.tpl",
  "LiveOrder.Model",
  "Ames.RentFromAnywhere.RentalLine",
  "Utils",
  "jQuery",
  "underscore"
], function (ames_rentfromanywhere_terms_tpl, LiveOrderModel, RentalLine, Utils, jQuery, _) {
  "use strict";

  var TERMS_URL = "https://www.shopamestools.com/rent-from-anywhere-terms-conditions";
  // Security deposit charged when the rental order is placed: % of the rental
  // charges (same value as DEPOSIT_PCT in UE_Rental_Items.js / custscript_rfa_deposit_pct)
  var DEPOSIT_PCT = 30;
  var ERROR_MESSAGE = "Please read and agree to the Rent From Anywhere Terms & Conditions to place your rental order.";

  // Rental detection + deposit base come from the shared strict rule
  // (Ames.RentFromAnywhere.RentalLine): configurable sets never match.
  var cartHasRentalLines = RentalLine.cartHasRentalLines;
  var rentalCharges = RentalLine.rentalCharges;

  function formatMoney(n) {
    try { return Utils.formatCurrency(Math.round(n * 100) / 100); } catch (e) { return "$" + (Math.round(n * 100) / 100).toFixed(2); }
  }

  // Shared state so the LiveOrder safety net below can read it
  var state = { active: false, agreed: false };

  // "Wizard.Module" exists ONLY in the checkout application bundle, while this
  // file ships in the shared extension bundle (shopping / myaccount too). So the
  // class is built lazily, the first time the checkout wizard instantiates the
  // module (Wizard.Step does `new (require(classname))(options)`). The exported
  // constructor is a thin proxy; in shopping / myaccount it is never called.
  var TermsClass = null;
  function buildClass() {
    if (TermsClass) return TermsClass;
    var WizardModule = require("Wizard.Module");
    TermsClass = WizardModule.extend({
    template: ames_rentfromanywhere_terms_tpl,
    className: "rfa-terms-module",

    events: {
      'change [data-action="rfa-agree-terms"]': "onToggle"
    },

    initialize: function () {
      WizardModule.prototype.initialize.apply(this, arguments);
      var self = this;
      this.liveOrder = (this.wizard && this.wizard.model) || LiveOrderModel.getInstance();
      // Agreement is per checkout session; re-render when the cart changes
      // so the module appears/disappears if rental lines are added/removed.
      this.liveOrder.on("change:lines sync", function () {
        state.active = cartHasRentalLines(self.liveOrder);
        if (self.$el && self.$el.length) self.render();
      });
    },

    // Only rental carts get the module
    isActive: function () {
      state.active = cartHasRentalLines(this.liveOrder);
      return state.active;
    },

    onToggle: function (e) {
      state.agreed = !!jQuery(e.currentTarget).prop("checked");
      this.$('[data-type="alert-placeholder-module"]').empty();
      this.$(".rfa-terms-module-row").toggleClass("rfa-terms-module-error", false);
    },

    isValid: function () {
      var deferred = jQuery.Deferred();
      if (!this.isActive() || state.agreed) return deferred.resolve().promise();

      this.$(".rfa-terms-module-row").addClass("rfa-terms-module-error");
      try { this.showError(ERROR_MESSAGE); } catch (e) { /* theme without placeholder */ }
      try {
        var top = this.$el.offset().top - 80;
        jQuery("html, body").animate({ scrollTop: top > 0 ? top : 0 }, 250);
      } catch (e2) { /* ignore */ }
      return deferred.reject().promise();
    },

    // Nothing to persist on the order; keep the wizard contract
    submit: function () {
      return jQuery.Deferred().resolve().promise();
    },

    render: function () {
      WizardModule.prototype.render.apply(this, arguments);
      // keep the box in sync with the agreement already given in this session
      this.$('[data-action="rfa-agree-terms"]').prop("checked", state.agreed);
      return this;
    },

    getContext: function () {
      var charges = rentalCharges(this.liveOrder);
      var deposit = Math.round(charges * DEPOSIT_PCT) / 100;
      return {
        termsUrl: TERMS_URL,
        agreed: state.agreed,
        depositPct: DEPOSIT_PCT,
        depositFormatted: formatMoney(deposit),
        rentalChargesFormatted: formatMoney(charges),
        remainingFormatted: formatMoney(charges - deposit),
        showDeposit: deposit > 0
      };
    }
    });
    return TermsClass;
  }

  // Exported constructor: builds the real Wizard.Module subclass on first use
  function TermsModule(options) {
    var Cls = buildClass();
    return new Cls(options);
  }

  // Register the module on the Review step. Called from the extension entry
  // point in the checkout application.
  TermsModule.register = function (container) {
    var checkout = container.getComponent("Checkout");
    if (!checkout || !_.isFunction(checkout.addModuleToStep)) return false;
    try {
      checkout.addModuleToStep({
        step_url: "review",
        module: {
          id: "RFATermsModule",
          index: 100, // after the review content, right above the bottom "Place Order"
          classname: "Ames.RentFromAnywhere.Terms.Module",
          // NO "container" option: Wizard.Step empties a container the first
          // time a module names it explicitly, which would wipe the other
          // review modules. Default = appended to #wizard-step-content.
          options: {}
        }
      });
    } catch (e) {
      console.warn("RentFromAnywhere: could not add Terms module to review step", e);
      return false;
    }

    // Safety net: even if some path submits the order without running the
    // step validation, a rental order is never placed without the agreement.
    var liveOrder = LiveOrderModel.getInstance();
    liveOrder.cancelableOn("before:LiveOrder.submit", function () {
      var defer = jQuery.Deferred();
      try {
        if (state.active && cartHasRentalLines(liveOrder) && !state.agreed) {
          defer.reject({ errorCode: "ERR_RFA_TERMS", errorMessage: ERROR_MESSAGE });
          return defer.promise();
        }
      } catch (e) { /* never block on a JS error */ }
      defer.resolve();
      return defer.promise();
    });
    return true;
  };

  return TermsModule;
});
